import GoogleMapReact, { Bounds } from 'google-map-react'
import _ from 'lodash'
import * as React from 'react'

import { SearchPage_result_location } from 'main/pages/Search/gql'
import BoundsControl, { SetBounds, SetBoundsType } from './BoundsControl'
import { boundsMargin, calloutHeight, googleMapStyles } from './constants'
import { FixedCalloutContainer, MapContainer } from './Map.styles'
import Marker from './Marker'
import { markerHeightForPin } from './Marker/util'
import { CalloutView, ICalloutOffset, IMarker, MapState } from './types'
import { relativeCalloutOffset } from './util'

const lego = window.lego
const DEFAULT_ZOOM = 3

// Center of the United States.
const DEFAULT_CENTER = {
  lat: 39.8283,
  lng: -98.5795,
}

interface IProps {
  bounds: Bounds | null
  calloutView: CalloutView
  fixedCalloutPadding: { bottom: number }
  markers: IMarker[]
  setBounds?: SetBounds
  onClick?: () => void
  location: SearchPage_result_location | null
}

interface IGoogleAPI {
  map: any
  maps: any
}

export default function Map(props: IProps) {
  const { bounds, calloutView, fixedCalloutPadding, markers, setBounds, onClick, location } = props

  const selectedMarker = markers.filter(marker => marker.selected)[0]
  const [state, setState] = React.useState<MapState | null>(null)
  const [googleApi, setGoogleApi] = React.useState<IGoogleAPI | null>(null)

  const [selectedMarkerCalloutOffset, setSelectedMarkerCalloutOffset] =
    React.useState<ICalloutOffset | null>(null)

  // Set the callout offset for the selected marker when the selected marker has changed. The
  // callout position for a marker is determined upon selection and should not change, e.g. when
  // moving the map.
  //
  // Note that (the existence of) the map state is a dependency of this effect to ensure that we
  // set the callout offset for a marker selected prior to the map state being available.
  React.useEffect(() => {
    if (!state) return

    setSelectedMarkerCalloutOffset(
      selectedMarker
        ? relativeCalloutOffset(state, selectedMarker.coordinates, selectedMarker.pin)
        : null,
    )
  }, [!!state, selectedMarker])

  // Fit bounds according to one of two strategies:
  // - When bounds are explicitly provided, fit map area to those bounds.
  // - Otherwise, fit map area around all provided markers (with some padding).
  //
  // This effect triggers when either of the following have changed:
  // - The existence of the loaded Google API.
  // - The provided bounds.
  // - The coordinates of any markers.
  React.useEffect(() => {
    if (bounds) {
      fitBounds()
      return
    }

    if (markers.length > 0) {
      fitBoundsToMarkers()
      return
    } else {
      fitBoundsToLocation()
      return
    }
  }, [
    !!googleApi,
    JSON.stringify(bounds),
    JSON.stringify(markers.map(marker => marker.coordinates)),
  ])

  return (
    <MapContainer>
      <GoogleMapReact
        bootstrapURLKeys={{ key: lego.config.GOOGLE_MAPS_KEY }}
        defaultCenter={DEFAULT_CENTER}
        defaultZoom={DEFAULT_ZOOM}
        onChange={handleChange}
        onClick={onClick}
        onGoogleApiLoaded={handleGoogleApiLoaded}
        yesIWantToUseGoogleMapApiInternals
        options={{
          clickableIcons: false,
          fullscreenControl: false,
          maxZoom: 17,
          styles: googleMapStyles,
          zoomControlOptions: { position: 3 },
        }}
      >
        {state &&
          markers.map((marker, i) => (
            <Marker
              key={i}
              lat={marker.coordinates.lat}
              lng={marker.coordinates.lng}
              callout={calloutView === CalloutView.Relative ? markerCallout(marker) : null}
              onClick={marker.onClick}
              pin={marker.pin}
              priority={marker.priority}
              selected={marker.selected}
            />
          ))}
      </GoogleMapReact>

      {selectedMarker && selectedMarker.callout && calloutView === CalloutView.Fixed && (
        <FixedCalloutContainer padding={fixedCalloutPadding}>
          {selectedMarker.callout}
        </FixedCalloutContainer>
      )}

      {setBounds && <BoundsControl mapState={state} setBounds={setBounds} />}
    </MapContainer>
  )

  function handleChange(stateChange: MapState) {
    setState(stateChange)

    if (setBounds && setBounds.__typename === SetBoundsType.OnMove && setBounds.enabled) {
      setBounds.onChangeBounds(stateChange.bounds)
    }
  }

  function handleGoogleApiLoaded({ map, maps }: any) {
    setGoogleApi({ map, maps })
  }

  function markerCallout(marker: IMarker) {
    const { callout, selected } = marker
    if (!callout || !selected || !selectedMarkerCalloutOffset) return null

    return { node: callout, offset: selectedMarkerCalloutOffset }
  }

  function fitBounds() {
    if (!googleApi) return
    if (!bounds) return

    const newBounds = new googleApi.maps.LatLngBounds()
    newBounds.extend(new googleApi.maps.LatLng(bounds.nw.lat, bounds.nw.lng))
    newBounds.extend(new googleApi.maps.LatLng(bounds.ne.lat, bounds.ne.lng))
    newBounds.extend(new googleApi.maps.LatLng(bounds.sw.lat, bounds.sw.lng))
    newBounds.extend(new googleApi.maps.LatLng(bounds.se.lat, bounds.se.lng))

    const padding = {
      bottom: 0,
      left: 0,
      right: 0,
      top: 0,
    }

    googleApi.map.fitBounds(newBounds, padding)
  }

  function fitBoundsToMarkers() {
    if (!googleApi) return
    if (!markers.length) return

    const markerHeight = maxMarkerHeight()

    const newBounds = new googleApi.maps.LatLngBounds()
    markers.forEach(marker => {
      newBounds.extend(new googleApi.maps.LatLng(marker.coordinates.lat, marker.coordinates.lng))
    })

    const padding = {
      bottom:
        boundsMargin +
        fixedCalloutPadding.bottom +
        (calloutView === CalloutView.Fixed ? calloutHeight + markerHeight : 0),
      left: boundsMargin,
      right: boundsMargin,
      top: boundsMargin,
    }

    googleApi.map.fitBounds(newBounds, padding)
  }

  function fitBoundsToLocation() {
    if (!googleApi) return
    if (!location) return
    if (!location.viewport) return

    const viewport = location.viewport

    const newBounds = new googleApi.maps.LatLngBounds()
    newBounds.extend(
      new googleApi.maps.LatLng(viewport.southWest.latitude, viewport.southWest.longitude),
    )
    newBounds.extend(
      new googleApi.maps.LatLng(viewport.northEast.latitude, viewport.northEast.longitude),
    )

    const padding = {
      bottom: 0,
      left: 0,
      right: 0,
      top: 0,
    }

    googleApi.map.fitBounds(newBounds, padding)
  }

  function maxMarkerHeight() {
    return _.chain(markers)
      .map(marker => marker.pin)
      .uniqBy('_type')
      .map(pin => markerHeightForPin(pin))
      .max()
      .value()
  }
}

Map.defaultProps = {
  bounds: null,
  fixedCalloutPadding: { bottom: 0 },
}
